import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ShieldCheck, AlertTriangle, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FleetReconciliation({ token, onClose }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    axios.get(`${API}/fleet/reconciliation`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setReport(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Could not load the reconciliation report."))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  if (loading || !report) return <div className="flex justify-center py-8"><Loader2 className="animate-spin text-[#F2B705]" /></div>;
  const s = report.summary;

  return (
    <div data-testid="fleet-reconciliation-panel" className="bg-[#0C0C0C] border border-amber-500/25 rounded-xl p-4 mb-5 space-y-3">
      <div className="flex items-center gap-2">
        <h4 className="text-white font-bold text-sm flex items-center gap-2"><ShieldCheck size={14} className="text-amber-300" /> Fleet ↔ Bookings Reconciliation</h4>
        <span className="text-[9px] font-bold text-amber-300 border border-amber-500/40 rounded px-1.5 py-0.5">READ-ONLY — CHANGES NOTHING</span>
        <button data-testid="fleet-recon-close" onClick={onClose} className="ml-auto text-white/50 hover:text-white"><X size={14} /></button>
      </div>
      <div className="flex flex-wrap gap-3 text-xs">
        <span className="text-white/60">{s.vehicles} vehicles</span>
        <span className="text-white/60">{s.active_rentals} active rentals</span>
        <span data-testid="fleet-recon-mismatches" className={s.mismatches ? "text-red-300 font-bold" : "text-emerald-300"}>{s.mismatches} status mismatch(es)</span>
        <span className={s.unmatched_active_rentals ? "text-amber-300 font-bold" : "text-white/60"}>{s.unmatched_active_rentals} unmatched rental(s)</span>
        <span className="text-sky-300">{s.ready_to_offer} Ready to Offer</span>
      </div>
      <div className="space-y-1 max-h-64 overflow-y-auto pr-1">
        {report.vehicles.map((v) => (
          <div key={v.vehicle_id} data-testid={`fleet-recon-row-${v.vehicle_id}`}
            className={`rounded border px-2.5 py-1.5 text-[11px] ${v.mismatch ? "border-red-500/30 bg-red-500/[0.05]" : "border-white/8 bg-white/[0.02]"}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-white/80"><b>{v.vehicle}</b>{v.license_plate ? ` · ${v.license_plate}` : ""}</span>
              <span className="flex items-center gap-2">
                <span className="uppercase text-white/50">{v.fleet_status}</span>
                {v.mismatch && <span className="text-red-300 font-bold flex items-center gap-1"><AlertTriangle size={11} /> should be {v.expected_status.toUpperCase()}</span>}
                {v.ready_to_offer && <span className="text-[9px] text-sky-300 border border-sky-500/40 rounded px-1 py-0.5">READY TO OFFER</span>}
              </span>
            </div>
            {v.mismatch && <div className="text-red-300/80 mt-0.5">{v.note}</div>}
            {v.busy_bookings.map((b) => (
              <div key={b.booking_id} className="text-white/45 mt-0.5">↳ {b.name} · {b.pickup_date} → {b.return_date} ({b.state})</div>
            ))}
          </div>
        ))}
        {report.unmatched_bookings.map((u) => (
          <div key={u.booking_id} data-testid={`fleet-recon-unmatched-${u.booking_id}`} className="rounded border border-amber-500/30 bg-amber-500/[0.05] px-2.5 py-1.5 text-[11px] text-amber-300">
            ⚠ Active rental "{u.booking_vehicle}" ({u.name}, {u.pickup_date} → {u.return_date}) matches NO fleet vehicle — fix the booking's vehicle name.
          </div>
        ))}
      </div>
    </div>
  );
}
